import { FC, useCallback, useMemo } from 'react';
import { type DataGridFilterValue, DataGridFilterField } from '@ibsys/kit';
import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import { getAttributeValueFilter } from '@/entities/attributes';
import { useTimeDisplayFormat } from '@/entities/settings';
import {
  type AttributeFilter,
  attributeFilterToDataGridValue,
  dataGridValueToAttributeFilter,
} from '@/entities/dashboards';

type Props = {
  attribute: ConversationAttributeItem;
  filter: AttributeFilter;
  onChange: (next: AttributeFilter) => void;
};

/**
 * Поле ввода значения атрибутного фильтра.
 *
 * Это ровно то же поле, что и отбор по колонке атрибута в гриде диалогов: и вид условия
 * (`getAttributeValueFilter`), и сам редактор (`DataGridFilterField` из кита) — общие,
 * поэтому набор значения ведёт себя одинаково в гриде и в панелях фильтров.
 * Значение переводится в формат атрибутного фильтра бэкенда адаптерами `entities/dashboards`.
 *
 * Тумблер «не пусто» здесь не включается (`enableNotEmptyFilter` в конфиг не попадает):
 * контракт `filters` дашбордов и «Стратега» оператора не выражает.
 *
 * Лейбл компонента не задаётся: имя атрибута выводится во внешней шапке ячейки.
 */
export const AttributeFilterField: FC<Props> = ({ attribute, filter, onChange }) => {
  // Формат интервалов — тот же, что у отбора в таблицах: границы вводятся так же, как показаны
  const timeDisplayFormat = useTimeDisplayFormat('filter');
  const config = useMemo(() => getAttributeValueFilter(attribute, timeDisplayFormat), [attribute, timeDisplayFormat]);

  const value = useMemo(() => attributeFilterToDataGridValue(filter, config.type), [filter, config.type]);

  const path = filter.path;
  const handleChange = useCallback(
    (next: DataGridFilterValue | undefined) => onChange(dataGridValueToAttributeFilter(path, config.type, next)),
    [onChange, path, config.type],
  );

  return <DataGridFilterField config={config} value={value} onChange={handleChange} className="w-full" />;
};
